/* eslint-disable @typescript-eslint/no-explicit-any */
import { ASPECT_RATIO, IMG_TYPE_JPG, WIDE_ASPECT_RATIO } from '../constants/OffersHeroConstants';
import {
  ASPECT_RATIO_UXL_FIELD_MAPPING,
  constants,
  IMAGE_CACHE_DOMAIN,
  PRIMARY_MEDIA,
  TERTIARY_MEDIA,
  SECONDARY_MEDIA,
  WIDE_HOR_IMG,
  LOCALE_SUBDIRECTORY_MAPPING,
} from '../constants/CommonConstants';
import {
  generateI18Title,
  generateTitle,
  generateOfferUrl,
  getCookieValue,
  extractValue,
  getFallbackImg,
} from './CommonUtils';
import { useTranslation } from 'react-i18next';
import {
  MARRIOTT_DOMAIN_CN,
  MARRIOTT_DOMAIN,
  MBOX_COOKIE,
  ASPECT_RATIO_SQUARE,
  ASPECT_RATIO_WIDE,
  CARD_LAYERED_VARIATION,
  CARD_LAYERED_WIDE,
  CARDSTYPE_VERTICAL,
  DESTINATION_OFFERS_RECIPE,
  IMAGE_ALT_TEXT,
  IMPRESSION_TRACK,
  LAYERED_WIDE,
  MERCHANDISING_CATEGORY,
  MEMBER_EXCLUSIVE,
  MERCHANDISING_OFFER,
} from '../constants/OfferCardConstants';
import DOMPurify from 'isomorphic-dompurify';
import { TimeLeft } from '../organisms/OffersMBEHero/OffersMBEHero.types';
import { CardVerticalVariations } from '@marriott/mi-ui-library';
import { OffersSearchInputVar, ResortRequestVariables } from '../organisms/OfferCardCarousel/OfferCardCarousel.types';
import config from '../../src/lib/config';
import { PRODUCT_AND_LOYALTY_OFFER } from '../constants/OfferDetailsConstants';

//SEO dynamic expression pattern for AEM model values
const { MARRIOTT_DEALS, REQUIRED_ASPECT_RATIO, NO_INDEX_NO_FOLLOW, NO_INDEX_FOLLOW, CHINESE_LANG } = constants;
const imageDomain = IMAGE_CACHE_DOMAIN;
const CHASE_RPC_CODES = new Set(['0513', '0532']);
const AMEX_RPC_CODES = new Set(['USPC', 'USMT', 'USBU']);

const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export const validateDetailsField = (value?: string): boolean => {
  if (value === null || value === '' || value === 'No Message Found') {
    return false;
  } else {
    return true;
  }
};

export const decodeHtml = (text: string): string => {
  if (typeof document !== 'undefined') {
    const fragment = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [
        'b',
        'strong',
        'i',
        'em',
        'u',
        's',
        'del',
        'sub',
        'sup',
        'mark',
        'small',
        'big',
        'ul',
        'ol',
        'li',
        'dl',
        'dt',
        'dd',
        'table',
        'tr',
        'td',
        'th',
        'thead',
        'tbody',
        'tfoot',
        'colgroup',
        'col',
        'caption',
        'p',
        'br',
        'hr',
        'blockquote',
        'pre',
        'a',
        'code',
        'span',
        'div',
        'img',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ],
      RETURN_DOM: true,
    }) as HTMLElement;
    // Convert the DOM Fragment to string
    let sanitizedHtml = fragment.innerHTML ?? '';
    // Replace sequences of multiple spaces with corresponding number of non-breaking spaces
    sanitizedHtml = sanitizedHtml.replace(/ {2,}/g, match => '&nbsp;'.repeat(match.length));
    return sanitizedHtml;
  }
  return text;
};

export const returnDecodedString = (input: string): string => {
  return input.replaceAll('&amp;', ' &');
};

/**
 * Formats a date string according to a format pattern.
 * Supported tokens: YYYY, MM, DD, Month (long), MonthShort (short)
 * Example format: "DD Month YYYY", "Month DD, YYYY", "YYYY年MonthShortDD日"
 */
export const getLocalizedDate = (
  date: string,
  lang: string | undefined,
  format: string // e.g., "DD Month YYYY"
): string => {
  if (!date || !format) return '';

  const [y, m, d] = date.split('-').map(Number);
  const currentDate = new Date(Date.UTC(y, m - 1, d));
  if (isNaN(currentDate.getTime())) {
    return '';
  }

  // Get date parts
  const day = String(currentDate.getUTCDate()).padStart(2, '0');
  const monthLong = new Intl.DateTimeFormat(lang, { month: 'long', timeZone: 'UTC' }).format(currentDate);
  const monthShort = new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(currentDate);
  let year = String(currentDate.getUTCFullYear());
  const yearShort = year.slice(-2);

  // Special handling for Thai Buddhist calendar
  if (lang === 'th-TH') {
    year = String(currentDate.getUTCFullYear() + 543);
  }

  // Replace tokens in format string
  const updatedDate = format
    .replace(/YYYY/g, year)
    .replace(/MM/g, String(currentDate.getMonth() + 1).padStart(2, '0'))
    .replace(/DD/g, day)
    .replace(/MonthShort/g, monthShort)
    .replace(/Month/g, monthLong)
    .replace(/YY/g, yearShort);

  return updatedDate.trim();
};

export const processDynamicProps = (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation();
  const marriottDealsLocalisedValues = {
    marriottDeals: t(MARRIOTT_DEALS),
    inText: t('in'),
  };
  const seoData = props?.productSpecificResponse;
  const apiData =
    props?.productSpecificResponse?.responseObject?.edges?.[0]?.node ??
    props?.productSpecificResponse?.responseObject?.offers?.[0];
  const facetData = props.productSpecificResponse?.responseObject?.facets;
  const languageCodes = props?.isPreview
    ? []
    : props?.productSpecificResponse?.responseObject?.offers?.[0]?.locales ??
      facetData?.filter((obj: any) => obj?.type?.description === 'locale')[0]?.buckets?.map((obj: any) => obj.code) ??
      [];
  const titleFormat = props.model?.metaProperties?.filter(
    (obj: any) => obj.name === 'og:title' && obj?.value?.trim() !== ''
  );
  const title = titleFormat?.length
    ? generateI18Title(apiData, titleFormat[0])
    : generateTitle(apiData, marriottDealsLocalisedValues);
  const imageArr = apiData?.photos?.images ?? [];
  const image = imageArr?.find((img: any) => img?.aspectRatio?.toUpperCase() === REQUIRED_ASPECT_RATIO);

  if (seoData?.marsha) {
    if (props?.sessionData?.cacheData?.data) {
      const sessionDataObj = props.sessionData.cacheData;
      const propHeaderObj = sessionDataObj.data;
      const ariesResObj = {
        propertyId: seoData.marsha,
        marshaRate: false,
        searchCriteriaChanged: false,
        numberOfRooms: 1,
        lengthOfStay: '1',
        rateListMenuViewType: 'rooms',
        singleDateLimit: 1728259199999,
        groupDateLimit: 1728259199999,
        availabilitySearchSourceHWS: false,
      };
      props['sessionData']['locale'] = props?.headersData[`accept-language`]
        ? props.headersData[`accept-language`].split(',')?.[0]
        : null;
      props['sessionData']['brandCode'] = props?.productSpecificResponse?.brand ?? '';
      propHeaderObj['AriesReservation'] = ariesResObj;
      sessionDataObj['data'] = propHeaderObj;
      props['sessionData']['cacheData'] = sessionDataObj;
    }
  }
  const updatedUrl: {
    url: string;
    hreflang: string;
  }[] = generateOfferUrl(
    apiData?.urlTitle ?? '',
    apiData?.id ?? '',
    apiData?.seoNickname ?? apiData?.propertiesData?.[0]?.seoNickname ?? '',
    apiData?.numProperties ?? '',
    '',
    [],
    seoData?.locale,
    seoData?.isPreview === 'true'
  );

  const hreflangList: {
    url: string;
    hreflang: string;
  }[] = generateOfferUrl(
    apiData?.urlTitle ?? '',
    apiData?.id ?? '',
    apiData?.seoNickname ?? apiData?.propertiesData?.[0]?.seoNickname ?? '',
    apiData?.numProperties ?? '',
    seoData?.marsha ?? '',
    languageCodes,
    seoData?.locale,
    seoData?.isPreview === 'true'
  );

  //temp mocking seo metadata
  const metaProperties = props.model?.metaProperties ?? [
    {
      name: 'og:title',
      value: '$title',
    },
    {
      name: 'og:description',
      value: '',
    },
    {
      name: 'imageURL',
      value: '',
    },
    {
      name: 'og:url',
      value: '',
    },
    {
      name: 'og:type',
      value: 'website',
    },
    {
      name: 'og:site_name',
      value: 'Marriott International',
    },
    {
      name: 'fbAppId',
      value: '311568359040551',
    },
  ];

  const metaNames = props.model?.metaNames ?? [
    {
      name: 'keyword',
      value: '',
    },
    {
      name: 'msapplication-TileColor',
      value: '',
    },
    {
      name: 'msapplication-TileImage',
      value: '',
    },
    {
      name: 'onLoadLightboxURI',
      value: '',
    },
    {
      name: 'description',
      value: '',
    },
    {
      name: 'showLightboxOnLoad',
      value: '',
    },
    {
      name: 'robots',
      value: 'index,follow',
    },
  ];

  const mappedProperties = metaProperties.map((meta: { name: string; value: string }) => {
    if (meta.name.includes('og:title')) {
      meta.value = title;
      return meta;
    }
    if (/og:description|description/.test(meta.name)) {
      meta.value = apiData?.descriptionTeaser ?? '';
      return meta;
    }
    if (/og:image|imageURL/.test(meta.name)) {
      meta.value = `${imageDomain}${image?.url}`;
      return meta;
    }
    if (/og:url/.test(meta.name)) {
      meta.value = updatedUrl ? updatedUrl?.[0]?.['url'] : '';
      return meta;
    }
    if (meta.value.trim() === '' && apiData?.[`${meta.name}`]) {
      meta.value = apiData?.[`${meta.name}`];
    }
    return meta;
  });

  const mappedMetaNames = metaNames.map((meta: { name: string; value: string }) => {
    //AEM send "index,follow" by default
    if (meta.name === 'robots' && !apiData?.searchable) {
      meta.value = NO_INDEX_NO_FOLLOW;
      return meta;
    }
    if (/og:description|description/.test(meta.name)) {
      meta.value = apiData?.descriptionTeaser ?? '';
      return meta;
    }
    if (meta.value.trim() === '' && apiData?.[`${meta.name}`]) {
      meta.value = apiData[`${meta.name}`];
    }
    return meta;
  });
  const hreflangUrls = hreflangList?.map(item => ({
    href: item.url,
    hreflang: item.hreflang,
  }));
  props.model['metaProperties'] = mappedProperties;
  props.model['metaNames'] = mappedMetaNames;
  props.model['title'] = title;
  props.model['canonical'] = updatedUrl ? updatedUrl?.[0]?.['url'] : '';
  props.model['hreflangList'] = hreflangUrls;
};

export const updateDataLayer = (servicedata: any) => {
  const dataLayer: any = {};
  const apiData = servicedata?.responseObject?.offers?.[0] ?? servicedata?.responseObject?.edges?.[0]?.node;
  const uri = servicedata?.uri ?? null;
  const partProperties = apiData?.propertiesData?.[0] ?? apiData?.participatingProperties ?? {};
  dataLayer['brndCode'] = servicedata?.brand;
  dataLayer['hotelLoc'] = Object.keys(partProperties)?.length ? processLoc(partProperties, ',') : '';
  dataLayer['mrshaCode'] = servicedata?.marsha;
  dataLayer['offer_title'] = apiData?.title;
  dataLayer['promo_single_or_multi_property'] = uri !== null ? checkOfferType(uri) : '';
  dataLayer['promo_special_offer_id'] = servicedata?.offerId;

  dataLayer['prop_address_city'] = partProperties?.contactInformation?.address?.city ?? '';
  dataLayer['prop_address_city_state_country'] = Object.keys(partProperties)?.length
    ? processLoc(partProperties, '|')
    : '';
  dataLayer['prop_address_country_abbr'] = partProperties?.contactInformation?.address?.country?.code ?? '';
  dataLayer['prop_brand_code'] = servicedata?.brand;
  dataLayer['prop_brand_name'] = Object.keys(partProperties)?.length
    ? partProperties?.basicInformation?.brand?.name
    : '';
  dataLayer['prop_marsha_code'] = servicedata?.marsha;
  dataLayer['prop_name'] = partProperties?.name ?? partProperties?.basicInformation?.name;
  dataLayer['prop_currency_type'] = partProperties?.currency ?? partProperties?.basicInformation?.currency;
  dataLayer['prop_address_state_abbr'] = partProperties?.contactInformation?.address?.stateProvince?.code ?? '';
  dataLayer['prop_address_state_name'] =
    partProperties?.contactInformation?.address?.stateProvince?.description ??
    partProperties?.contactInformation?.address?.stateProvince ??
    '';

  dataLayer['search_cluster_code'] = apiData?.clusterCode;
  return dataLayer;
};

const processLoc = (obj: any, separator: string) => {
  const locArr: string[] = [];

  const address = obj?.contactInformation?.address ?? obj?.address;
  const getLocValue = (value: any): string => {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object') {
      const normalized = value?.description ?? value?.code ?? '';
      return typeof normalized === 'string' ? normalized.trim() : '';
    }
    return '';
  };

  ['city', 'stateProvince', 'country'].forEach(item => {
    const locValue = getLocValue(address?.[item]);
    if (locValue !== '') {
      locArr.push(locValue);
    }
  });

  return locArr.join(separator);
};

const checkOfferType = (uri: string): string => {
  if (!uri) return '';
  const lowerUri = uri?.toLowerCase();
  const typeMap: Record<string, string> = {
    'offers-spo': 'SPO_OFFER',
    'offers-mpo': 'MPO_OFFER',
    'offers-apo': 'ANC_OFFER',
    'offers-dme-single-card': 'DME_SINGLE_CARD_OFFER',
  };
  for (const key in typeMap) {
    if (lowerUri.includes(key)) {
      return typeMap[key];
    }
  }
  return '';
};

export const isValidUrl = (urlString: string) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

export const processDomainUrl = (url: string) => {
  let modUrl = url;
  if (isValidUrl(url)) {
    if (url.includes('/offers/')) {
      modUrl = '/offers/' + url.split('/offers/').pop();
    }
  }
  return modUrl;
};

export const makeDomainSpecificContent = (content = '') => {
  try {
    const url = new URL(content);
    if (url.hostname === MARRIOTT_DOMAIN || url.hostname === MARRIOTT_DOMAIN_CN) {
      return url.pathname + url.search + url.hash;
    }
  } catch {
    // Not a valid URL, return as is
  }
  return content;
};

let SUBDIRECTORY_PREFIX = '';

export const setOffersSubDirectoryPrefix = (prefix = '') => (SUBDIRECTORY_PREFIX = prefix);

export const addSubDirectoryPrefix = (url = '') =>
  SUBDIRECTORY_PREFIX &&
  url?.startsWith('/') &&
  url?.substring(1, findNthOccurence(url, 1, '/'))?.toLowerCase() !== SUBDIRECTORY_PREFIX?.toLowerCase()
    ? SUBDIRECTORY_PREFIX
      ? '/' + SUBDIRECTORY_PREFIX + url
      : url
    : url;

const findNthOccurence = (str: string, nth: number, char: string) => {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) index = str.indexOf(char, index + 1);
  }
  return index;
};

export const setSessionKey = (key: string, value: string) => {
  if (canUseDOM) {
    sessionStorage.removeItem(key);
    sessionStorage.setItem(key, value);
  }
};

export const processAcceptLang = (locale: string) => {
  return locale ? (locale?.includes(',') ? locale?.split(',')[0] : locale?.replace('_', '-')) : locale;
};

export const checkEliteUser = (datalayer: any) => {
  let rewardMemberLevel =
    datalayer?.['mr_id'] && datalayer?.['mr_prof_rewards_level'] ? datalayer?.['mr_prof_rewards_level'] : '';
  let isEliteUser = false;
  if (rewardMemberLevel) {
    rewardMemberLevel = (rewardMemberLevel as string).split(' ')?.map((item: string) => item.trim())[0];
    isEliteUser = true;
  }
  return { isEliteUser, memberLevel: rewardMemberLevel };
};

const getPosition = (string: string, subString: string, index: number) => {
  return string?.split(subString, index).join(subString).length;
};
const getImagePath = (ren: any, imagePath: string, imageDomainVal: any) => {
  const imageDomainPath = imageDomainVal ?? imageDomain;
  let referencePath = ren?.renditionPath.slice(0, ren?.renditionPath.indexOf(','));
  referencePath = referencePath.includes('https') ? referencePath : `${imageDomainPath}${referencePath}`;
  const urlParams = new URL(referencePath).searchParams;
  const width = ren?.width && ren?.width !== 0 ? ren?.width : urlParams.get('wid');
  const height = ren?.height && ren?.height !== 0 ? ren?.height : urlParams.get('hei') ?? '';
  const fit = urlParams.get('fit');
  const rendition =
    imageDomainPath + imagePath + '?wid=' + width + (height ? '&hei=' + height : '') + (fit ? '&fit=' + fit : '');
  const rendition2x =
    imageDomainPath +
    imagePath +
    '?wid=' +
    2 * width +
    (height ? '&hei=' + 2 * height : '') +
    (fit ? '&fit=' + fit : '');
  return `${rendition}, ${rendition2x} 2x`;
};

export const getMediaSource = (offersDataMedia: any, expectedSource: any, aspectRatio?: any): string => {
  //updated logic
  if (expectedSource === PRIMARY_MEDIA) {
    let primaryMatchCount = 0;
    for (let i = 0; i <= aspectRatio?.length; i++) {
      if (offersDataMedia?.primaryImage?.imageUrls?.[aspectRatio[i]]) {
        primaryMatchCount++;
      }
    }
    if (primaryMatchCount) {
      return PRIMARY_MEDIA;
    } else {
      return 'model';
    }
  } else {
    if (offersDataMedia?.secondaryImage?.imageSrc) {
      return SECONDARY_MEDIA;
    } else {
      return 'model';
    }
  }
};

export const getUXLMediaSource = (uxlMedia: any, expectedSource: string): string => {
  if (expectedSource === SECONDARY_MEDIA) {
    if (uxlMedia?.secondaryImage?.imageSrc) {
      return SECONDARY_MEDIA;
    } else if (uxlMedia?.primaryImage?.imageSrc) {
      return PRIMARY_MEDIA;
    } else {
      return 'model';
    }
  } else if (expectedSource === PRIMARY_MEDIA) {
    if (uxlMedia?.primaryImage?.imageSrc) {
      return PRIMARY_MEDIA;
    } else {
      return 'model';
    }
  } else {
    return 'model';
  }
};

export const getMediaDataSecondary = (media: any, model: any, imageDomainVal: any, mediaSource: string) => {
  const imageDomainPath = imageDomainVal ?? imageDomain;
  const isChinaDomain = imageDomainPath === 'https://cache.marriott.com.cn';
  const imgSrcPath = isChinaDomain
    ? media?.secondaryImage?.imageSrc?.includes('zh_cn')
      ? media?.secondaryImage?.imageSrc
      : getFallbackImg(config.FALLBACK_CN_IMG_URI, WIDE_ASPECT_RATIO)
    : media?.secondaryImage?.imageSrc ?? '';
  return {
    renditions: [],
    fileReferenceBackgroundImage: mediaSource === SECONDARY_MEDIA ? media?.secondaryImage?.imageSrc : '',
    altText:
      mediaSource === PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource === SECONDARY_MEDIA
        ? media?.secondaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: imgSrcPath ? `${imageDomainPath}${imgSrcPath}` : model?.assetPath,
  };
};

export const getMediaData = (
  media: any,
  model: any,
  mediaSource: string,
  expectedMedia: string,
  imageDomainVal: any,
  asptRatio?: string
) => {
  const mediaData =
    mediaSource === PRIMARY_MEDIA
      ? media?.primaryImage
      : mediaSource === SECONDARY_MEDIA
      ? media?.secondaryImage
      : null;
  const imgAspectRatio = asptRatio ?? WIDE_HOR_IMG;
  const imageDomainPath = imageDomainVal ?? imageDomain;
  const renditionsArr = [];
  let imgSrcPath = '';
  if (expectedMedia === PRIMARY_MEDIA) {
    for (let i = 0; i < model?.renditions?.length; i++) {
      const ren = model?.renditions[i];
      const aspectRatio = ren?.renditionPath?.slice(
        getPosition(ren?.renditionPath, ':', 2) + 1,
        ren?.renditionPath?.indexOf('?')
      );
      const key = aspectRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
      const imagePath = mediaData?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]];
      const renditionObj = {
        renditionPath: getImagePath(ren, imagePath, imageDomainPath),
        mediaValue: ren?.mediaValue,
        width: ren?.width,
        dynamic: ren?.dynamic,
        mediaQuery: ren?.mediaQuery,
        height: ren?.height,
        damPath: '',
      };
      renditionsArr.push(renditionObj);
    }
  }

  const key = imgAspectRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
  const isChinaDomain = imageDomainPath === 'https://cache.marriott.com.cn';
  imgSrcPath =
    mediaSource === PRIMARY_MEDIA
      ? mediaData?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[key]] ??
        renditionsArr?.[0]?.renditionPath?.slice(0, getPosition(renditionsArr[0]?.renditionPath, ':', 2)) ??
        mediaData?.imageSrc
      : mediaSource === SECONDARY_MEDIA
      ? isChinaDomain
        ? media?.secondaryImage?.imageSrc?.includes('zh_cn')
          ? media?.secondaryImage?.imageSrc
          : getFallbackImg(config.FALLBACK_CN_IMG_URI, WIDE_ASPECT_RATIO)
        : media?.secondaryImage?.imageSrc ?? ''
      : '';
  return {
    renditions: renditionsArr,
    fileReferenceBackgroundImage:
      mediaSource === PRIMARY_MEDIA
        ? media?.primaryImage?.imageSrc
        : mediaSource === SECONDARY_MEDIA
        ? media?.secondaryImage?.imageSrc
        : '',
    altText:
      mediaSource === PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource === SECONDARY_MEDIA
        ? media?.secondaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: imgSrcPath ? `${imageDomainPath}${imgSrcPath}` : model?.assetPath,
  };
};
export const getEdgeHost = (cookies: string) => {
  const cookieString = decodeURIComponent(cookies);
  const strValue = getCookieValue(cookieString, MBOX_COOKIE);
  const match = extractValue(strValue, 'PC#', '#') ?? '';
  let edgeValue = '';
  if (match.split('.').length > 1 && match.split('.')[1].split('_').length > 1) {
    edgeValue = match.split('.')[1].split('_')[0];

    return edgeValue;
  } else return null;
};

export const getImageWithDomain = (imgSrc: string, imageDomainVal: any): string => {
  if (imgSrc) {
    return `${imageDomainVal ?? imageDomain + imgSrc}`;
  } else {
    return '';
  }
};

export const getParamValue = (urlParams: any, paramVal: string) => {
  return urlParams?.has(paramVal) && urlParams?.get(paramVal) !== null && urlParams?.get(paramVal) !== ''
    ? urlParams?.get(paramVal)
    : '';
};

export const checkParamValue = (paramVal: string, urlParams?: any) => {
  if (urlParams) {
    return urlParams?.has(paramVal) && urlParams?.get(paramVal) !== null && urlParams?.get(paramVal) !== ''
      ? true
      : false;
  } else {
    return paramVal !== null && paramVal !== '' && paramVal !== undefined ? true : false;
  }
};

export const getDeepLinkParams = (urlParams: any, ariesSearch?: any) => {
  let searchByGeoLoc: boolean = false;
  let searchBystate: boolean = false;
  let searchBycountry: boolean = false;
  let searchByOffersIds: boolean = false;
  let offerIds: string[] | undefined = [];
  let isEmptyOfferIds: boolean = false;
  let propertyCodes: string[] | undefined = [];
  let searchByPropertyCodes: boolean = false;
  let isEmptyPropertyCodes: boolean = false;
  let searchByStayBasedOfferFlag: boolean = false;
  let isEmptyStayBasedOfferFlag: boolean = false;
  let destinationCitySearched: string | null = '';
  let hasPlaceId: string | null = '';
  let destinationSearched: string | null = null;
  let notEligible: boolean = false;
  let invalidUser: boolean = false;
  let invalidDeal: boolean = false;
  let numAdultsPerRoom: string | null = '1';
  let numChildrenPerRoom: string | null = '0';
  let numGuestsPerRoom: string | null = '1';
  let numRooms: string | null = '1';
  notEligible = urlParams?.has('notEligible') ? true : false;
  invalidUser = urlParams?.has('invalidUserMsg') ? true : false;
  invalidDeal = urlParams?.has('invalidDealMsg') ? true : false;
  hasPlaceId = getParamValue(urlParams, 'destinationAddress.placeId');
  destinationSearched = getParamValue(urlParams, 'destinationAddress.destination');
  destinationCitySearched = getParamValue(urlParams, 'destinationAddress.city');
  searchByGeoLoc =
    (checkParamValue('destinationAddress.latitude', urlParams) &&
      checkParamValue('destinationAddress.longitude', urlParams) &&
      urlParams?.get('destinationAddress.type') !== 'Country') ||
    (checkParamValue(ariesSearch?.latitude) &&
      checkParamValue(ariesSearch?.longitude) &&
      checkParamValue(ariesSearch?.destinationType) &&
      ariesSearch?.destinationType !== 'Country');

  searchBystate =
    checkParamValue(ariesSearch?.stateProvince) || checkParamValue('destinationAddress.stateProvince', urlParams);

  searchBycountry = checkParamValue(ariesSearch?.country) || checkParamValue('destinationAddress.country', urlParams);
  offerIds = urlParams?.has('offers') && urlParams?.get('offers') !== '' ? urlParams?.get('offers')?.split(',') : [];
  searchByOffersIds =
    urlParams?.has('offers') && urlParams?.get('offers') !== '' && urlParams?.get('offers')?.split(',')?.length
      ? true
      : false;
  isEmptyOfferIds = urlParams?.has('offers') && urlParams?.get('offers') === '';
  propertyCodes =
    urlParams?.has('propertycodes') && urlParams?.get('propertycodes') !== ''
      ? urlParams?.get('propertycodes')?.split(',')
      : urlParams?.has('propertyCode') && urlParams?.get('propertyCode') !== ''
      ? urlParams?.get('propertyCode')?.split(',')
      : [];

  searchByPropertyCodes =
    (urlParams?.has('propertycodes') &&
      urlParams?.get('propertycodes') !== '' &&
      urlParams?.get('propertycodes')?.split(',').length) ||
    (urlParams?.has('propertyCode') &&
      urlParams?.get('propertyCode') !== '' &&
      urlParams?.get('propertyCode')?.split(',').length)
      ? true
      : false;
  isEmptyPropertyCodes =
    (urlParams?.has('propertycodes') && urlParams?.get('propertycodes') === '') ||
    (urlParams?.has('propertyCode') && urlParams?.get('propertyCode') === '');
  searchByStayBasedOfferFlag = urlParams?.has('stayBasedOffer') && urlParams?.get('stayBasedOffer') === 'false';
  isEmptyStayBasedOfferFlag = urlParams?.has('stayBasedOffer') && urlParams?.get('stayBasedOffer') === '';
  numAdultsPerRoom = urlParams?.has('numAdultsPerRoom') ? urlParams?.get('numAdultsPerRoom') : '1';
  numChildrenPerRoom = urlParams?.has('childrenCount') ? urlParams?.get('childrenCount') : '0';
  numGuestsPerRoom = urlParams?.has('guestCountBox') ? urlParams?.get('guestCountBox') : '1';
  numRooms = urlParams?.has('roomCount') ? urlParams?.get('roomCount') : '1';
  const deepLinkParamObj = {
    isEmptyStayBasedOfferFlag: isEmptyStayBasedOfferFlag,
    searchByStayBasedOfferFlag: searchByStayBasedOfferFlag,
    isEmptyPropertyCodes: isEmptyPropertyCodes,
    searchByPropertyCodes: searchByPropertyCodes,
    propertyCodes: propertyCodes,
    isEmptyOfferIds: isEmptyOfferIds,
    searchByOffersIds: searchByOffersIds,
    offerIds: offerIds,
    searchBycountry: searchBycountry,
    searchBystate: searchBystate,
    searchByGeoLoc: searchByGeoLoc,
    destinationCitySearched: destinationCitySearched,
    destinationSearched: destinationSearched,
    hasPlaceId: hasPlaceId,
    notEligible: notEligible,
    invalidUser: invalidUser,
    invalidDeal: invalidDeal,
    numAdultsPerRoom: numAdultsPerRoom,
    numChildrenPerRoom: numChildrenPerRoom,
    numGuestsPerRoom: numGuestsPerRoom,
    numRooms: numRooms,
  };
  return deepLinkParamObj;
};

export const hasDeepLink = (query: any) => {
  if (query?.propertyCode || query?.propertycodes || query?.stayBasedOffer || query?.offers) {
    return true;
  } else return false;
};

export const generateUXLVariablesForDestination = (
  latitude: string,
  longitude: string,
  stateCode: any,
  countryCode: any,
  isStateEnabled: any,
  isCountryEnabled: any,
  isPOI: any
) => {
  const obj: any = {
    limit: 15,
    offset: 0,
    participatingPropertiesLimit2: 1,
  };
  if (isPOI || (!isStateEnabled && !isCountryEnabled)) {
    return {
      ...obj,
      input: {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
      },
      sort: {
        field: 'OFFER_DISTANCE',
      },
    };
  } else if (isStateEnabled && !isCountryEnabled) {
    return {
      ...obj,
      input: {
        state: stateCode,
        country: countryCode,
      },
      sort: {
        field: 'BOOKING_END_DATE',
      },
    };
  } else if (!isStateEnabled && isCountryEnabled) {
    return {
      ...obj,
      input: {
        country: countryCode,
      },
      sort: {
        field: 'BOOKING_END_DATE',
      },
    };
  }
};
const brandLabelMappings: Record<string, string> = {
  luxuryBrands: 'offersSelectAllLuxuryBrands',
  premiumBrands: 'offersSelectAllPremiumBrands',
  selectBrands: 'offersSelectAllSelectBrands',
  longerStayBrands: 'offersSelectAllLongerStaysBrands',
};
export const getBrandCategorySelectAllLabel = (category: string): string => {
  return brandLabelMappings[category] || '';
};

export const getControlsData = (currentFilterData: any) => {
  const desiredKeys = ['code', 'count', 'label', 'description'];
  const updatedFilterOptions = currentFilterData?.map((obj: any) => {
    const newFilterOptionsObj: any = {};
    desiredKeys?.forEach(key => {
      newFilterOptionsObj[key] = obj[key];
    });
    const modifiedLabel = obj?.description;
    return { ...newFilterOptionsObj, label: modifiedLabel };
  });
  return updatedFilterOptions;
};
export const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(today.getDate()).padStart(2, '0'); // Ensure two digits

  return `${year}-${month}-${day}`;
};
export const getTomorrowDate = (fromOSERP: boolean = false) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1); // Add one day

  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(tomorrow.getDate()).padStart(2, '0'); // Ensure two digits

  return fromOSERP ? `${year}/${month}/${day}` : `${year}-${month}-${day}`;
};

export const getOffersDateFormatted = (date: any): string => {
  return date?.format('MM/DD/YYYY') ?? '';
};

export const calculateTimeLeft = (startDate: string, isAuthorMode: boolean): TimeLeft => {
  if (isAuthorMode) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const start = new Date(startDate);
  const now = new Date();
  const difference = start.getTime() - now.getTime();

  if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

export const checkCampaignStatus = (
  campaignStartDate: string,
  campaignEndDate: string,
  cycleState: string | null = '',
  isPreview?: string | null
): string => {
  const localTime = new Date();
  const startTime = new Date(Date.parse(campaignStartDate)).getTime();
  const endTime = new Date(Date.parse(campaignEndDate)).getTime();
  const currentTime = localTime.getTime();
  let state: string = '';

  if (isPreview === 'true' && cycleState) {
    state = cycleState === 'on' ? constants.CAMPAIGN_ACTIVE : constants.CAMPAIGN_INACTIVE;
  } else {
    state = currentTime >= startTime && currentTime < endTime ? constants.CAMPAIGN_ACTIVE : constants.CAMPAIGN_INACTIVE;
  }
  return state;
};

export const isUserAuthenticated = (data: any) => {
  if (!data) {
    return false;
  }

  const { memState = '', mr_id = null } = data;
  return mr_id !== null && memState === 'authenticated';
}; // Default to unauthenticated

// common functions for offercard and offercardCarousel
export const trackingPropertiesObj = (modelProp: any) => {
  const {
    trackingContentPosition,
    trackingOfferType,
    atCCeVar48,
    trackingDescription,
    trackingTag,
    clickTrack,
    impressionEventType,
    merchandisingCategory,
    additionalTrackingVariables,
    location,
    payloadType,
    compName,
    enableScrollingBehavior,
    cardLocation,
    leftArrowDesc,
    rightArrowDesc,
    description,
  } = modelProp?.trackingProperties || {};

  return {
    trackingContentPosition,
    trackingOfferType,
    atCCeVar48,
    trackingDescription,
    trackingTag,
    isCoBrand: false,
    clickTrack,
    impressionTrack: true,
    impressionCount: true,
    impressionEventType,
    merchandisingCategory,
    additionalTrackingVariables,
    location,
    payloadType,
    compName,
    enableScrollingBehavior,
    cardLocation,
    leftArrowDesc,
    rightArrowDesc,
    description,
  };
};
export const isMemberExclusive = (memberLevel: Array<string>): boolean => {
  if (memberLevel?.find(ele => MEMBER_EXCLUSIVE.includes(ele))) {
    return true;
  }
  return false;
};
export const processOfferCarousel = (
  data: any,
  model: any,
  isRecipeEliteExclusive: any,
  envVarsObject: any,
  cardType: string,
  openInaNewTab: any,
  isPreview?: boolean,
  isLightning = false
) => {
  const count = data?.offersData?.length;
  const cardKeys = Object?.keys(data?.offersData);
  const imageDomainVal = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'] ?? imageDomain;

  const offerMap = data?.offersData?.map((card: any, index: number) => {
    let imageUri = null;
    let imageUriLg = null;
    const sizeParams = isPreview ? ' 2x' : '';
    const baseUri = `${imageDomainVal}${card.image}`;
    if (card.image.includes('/is/image')) {
      imageUri = `${baseUri}?wid=365&fit=constrain${sizeParams}`;
      imageUriLg = `${baseUri}?wid=800&fit=constrain${sizeParams}`;
    } else {
      imageUri = `${baseUri}?downsize=365px:*${sizeParams}`;
      imageUriLg = `${baseUri}?downsize=800px:*${sizeParams}`;
    }

    const isMerchandisingOffer = card.parentOfferType === MERCHANDISING_OFFER;
    const eyeBrowText = isMerchandisingOffer
      ? card?.descriptionTeaser
      : card.propertiesCount === 1
      ? card.propertyName
      : model?.eyebrow;

    const MemberExclusive = isMemberExclusive(card.memberLevel) ?? false;
    const badgeText = isMerchandisingOffer
      ? isLightning
        ? card.flag
        : card?.offerCategory
      : MemberExclusive
      ? model?.memberExclusive
      : '';
    const cardLocDetails = model?.trackingProperties?.location
      ? `${model.trackingProperties.location}-card-${index}`
      : `card-${index}`;
    const cardTrackingTag = model?.trackingProperties?.trackingTag
      ? `${model?.trackingProperties?.trackingTag}-${card.catalogId}`
      : card.catalogId || null;
    const cardTrackingOfferType = model?.trackingProperties?.trackingOfferType
      ? `${model?.trackingProperties?.trackingOfferType}-${card.parentOfferType}`
      : card.parentOfferType || null;
    const isCardVertical = cardType === CARDSTYPE_VERTICAL;
    const cardObj = {
      ...(isCardVertical && {
        cardVerticalVariations: CardVerticalVariations.Tall1x1,
        ':type': 'mi-aem-common-spa/components/content/cardvertical',
        description: card.description,
        ctaLinkText: model?.readMore || 'Read More',
      }),
      ...(!isCardVertical && {
        cardVerticalVariations: CardVerticalVariations.Standard,
        cardLayeredVariations: cardType === LAYERED_WIDE ? CARD_LAYERED_WIDE : CARD_LAYERED_VARIATION,
        ':type': 'mi-aem-common-spa/components/content/cardlayered',
        description: card?.header,
        eyebrow: eyeBrowText,
      }),
    };

    return {
      ':items': {
        cardlayered_copy: {
          trackingProperties: {
            atCCeVar48: model?.trackingProperties?.atCCeVar48 || null,
            additionalTrackingVariables: model?.trackingProperties?.additionalTrackingVariables || null,
            trackingDescription: card?.header,
            description: card?.header,
            clickTrack: model?.trackingProperties?.clickTrack ? true : false,
            impressionTrack: false,
            location: cardLocDetails,
            trackingContentPosition: cardLocDetails,
            merchandisingCategory: model?.trackingProperties?.merchandisingCategory
              ? model.trackingProperties.merchandisingCategory
              : 'nonCobrand',
            impressionCount: false,
            trackingTag: cardTrackingTag,
            trackingOfferType: cardTrackingOfferType,
          },
          styleclass: model?.cardTheme,
          headerTag: 'h3',
          icon: '',
          impressionTrack: IMPRESSION_TRACK,
          badgeIcon: !isRecipeEliteExclusive ? 'icon-lock' : '',
          imgAltText: card?.header ? card.header : IMAGE_ALT_TEXT,
          clickTrack: model?.trackingProperties?.clickTrack ? true : false,
          fileReferenceImage: `${imageDomainVal}${card.image}`,
          header: card.header,
          fontSize: 't-subtitle-l',
          [cardType === LAYERED_WIDE ? 'dynamicMediaWide' : 'dynamicMedia']: {
            altText: card?.header ? card.header : IMAGE_ALT_TEXT,
            assetPath: `${imageDomainVal}${card.image}`,
            dynamic: true,
            renditions: [
              {
                renditionPath: imageUriLg,
                mediaValue: '992px',
                width: 0,
                dynamic: true,
                damPath: `${imageDomainVal}${card.image}`,
                mediaQuery: 'min-width',
                height: 0,
              },
              {
                renditionPath: imageUri,
                mediaValue: '576px',
                width: 0,
                dynamic: true,
                damPath: `${imageDomainVal}${card.image}`,
                mediaQuery: 'min-width',
                height: 0,
              },
              {
                renditionPath: imageUri,
                mediaValue: '576px',
                width: 0,
                dynamic: true,
                damPath: `${imageDomainVal}${card.image}`,
                mediaQuery: 'max-width',
                height: 0,
              },
            ],
            damPath: card.image,
          },
          merchandisingCategory: MERCHANDISING_CATEGORY,
          impressionCount: IMPRESSION_TRACK,
          ctaLink: addSubDirectoryPrefix(makeDomainSpecificContent(card.ctaUrl)),
          badgeText: badgeText,
          logoIcon: '',
          openInNewTab: openInaNewTab,
          ...cardObj,
        },
      },
    };
  });
  return { count, cardKeys, offerMap };
};

export const processUxlData = (
  uxlData: any,
  isLightning: boolean,
  isFallback?: boolean,
  cardType?: string,
  model?: { maxOffersCount?: number },
  isPOI = false,
  isStateEnabled = false,
  isCountryEnabled = false,
  destinationName = '',
  imageDomain?: string
): { data: any; concatenatedOfferIds?: string } => {
  let response;
  let experienceId;
  let concatenatedOfferIds = '';
  if (isFallback) {
    response = uxlData?.fallbackOffers?.offerCollection || [];
  } else if (destinationName) {
    if (isPOI || (!isStateEnabled && !isCountryEnabled)) {
      response = uxlData?.offersSearchByGeolocation?.edges || [];
    } else {
      response = uxlData?.offersSearchByArea?.edges || [];
    }
  } else {
    response = isLightning
      ? uxlData?.marketing?.offers?.personalizedOffers?.offers?.length
        ? uxlData?.marketing?.offers?.personalizedOffers?.offers[0]?.offerCollection
        : []
      : (uxlData?.offersCarousel?.length && uxlData?.offersCarousel[0]?.offerCollection) || [];
    experienceId = isLightning
      ? uxlData?.marketing?.offers?.personalizedOffers?.offers?.length
        ? uxlData?.marketing?.offers?.personalizedOffers?.offers[0]?.experienceId
        : ''
      : uxlData?.offersCarousel?.length
      ? uxlData?.offersCarousel[0]?.experienceId
      : '';
  }

  const maxOffersCount = model?.maxOffersCount ?? response?.length;
  const ASPECT_RATIO_UXL_FIELD_MAPPING = {
    Wide: 'wideHorizontal',
    Square: 'square',
  };
  const imageRatio = cardType === LAYERED_WIDE ? ASPECT_RATIO_WIDE : ASPECT_RATIO_SQUARE;
  const isChinaDomain = imageDomain === 'https://cache.marriott.com.cn';
  const fallbackImg = getFallbackImg(
    isChinaDomain ? config?.FALLBACK_CN_IMG_URI : config?.FALLBACK_IMG_URI,
    cardType === LAYERED_WIDE ? ASPECT_RATIO_WIDE : ASPECT_RATIO_SQUARE
  );
  const defaultOffers = response
    ?.filter((offer: any) => offer?.offer?.title || offer?.node?.title)
    .slice(0, maxOffersCount)
    .map((item: any, index: number) => {
      const objKey = imageRatio as keyof typeof ASPECT_RATIO_UXL_FIELD_MAPPING;
      const primaryImageSrc = item?.offer?.media?.primaryImage?.imageSrc || item?.node?.media?.primaryImage?.imageSrc;
      const isValidCnImg = primaryImageSrc?.includes('zh_cn');
      const DACImagePath = isChinaDomain
        ? isValidCnImg
          ? item?.offer?.media?.primaryImage?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]] ||
            item?.node?.media?.primaryImage?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]]
          : fallbackImg
        : item?.offer?.media?.primaryImage?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]] ||
          item?.node?.media?.primaryImage?.imageUrls?.[ASPECT_RATIO_UXL_FIELD_MAPPING?.[objKey]];
      let imageUrl = '';

      if (DACImagePath) {
        imageUrl = DACImagePath;
      } else {
        imageUrl = fallbackImg;
      }

      if (index !== 0) {
        concatenatedOfferIds += ',';
      }
      concatenatedOfferIds += item?.offer?.id ?? item?.node?.id;
      const isProductOrLoyaltyOffer = (offerType: string) => offerType === PRODUCT_AND_LOYALTY_OFFER;
      const isMerchandisingOffer = (offer: { parentOfferType: string }) =>
        offer?.parentOfferType === MERCHANDISING_OFFER;
      return {
        image: imageUrl,
        header: item?.offer?.title || item?.node?.title,
        propertyName: isLightning
          ? item?.offer?.propertiesData?.[0]?.basicInformation?.name || ''
          : item?.offer?.participatingProperties?.properties?.[0]?.basicInformation?.name ||
            item?.node?.participatingProperties?.properties?.[0]?.basicInformation?.name,
        ctaUrl:
          isProductOrLoyaltyOffer(item?.offer?.type) ||
          isProductOrLoyaltyOffer(item?.node?.offerType) ||
          isMerchandisingOffer(item?.offer) ||
          isMerchandisingOffer(item?.node)
            ? item?.offer?.ctaLink || item?.node?.callToActionLink
            : item?.offer?.url || item?.node?.url,
        propertiesCount: item?.offer?.numProperties || item?.node?.numProperties,
        memberLevel: item?.offer?.memberLevel || item?.node?.memberLevel,
        description: item?.offer?.description || item?.node?.description,
        catalogId: item?.catalogId,
        parentOfferType: item?.offer?.parentOfferType || item?.node?.parentOfferType,
        descriptionTeaser: item?.offer?.descriptionTeaser || item?.node?.descriptionTeaser,
        offerCategory: isLightning ? item?.offer?.flag : item?.offer?.offerCategory || item?.node?.offerCategory,
      };
    });
  return {
    data: { offersData: defaultOffers, isFallback, experienceId },
    concatenatedOfferIds: !destinationName ? concatenatedOfferIds : '',
  };
};
export const checkNoDataAvailable = (
  queryData: any,
  isLightning: boolean,
  isPreview?: boolean,
  defaultOffersReceipe?: any,
  noOfCards?: any,
  isPOI = false,
  isStateEnabled = false,
  isCountryEnabled = false
) => {
  if (isPreview) {
    if (!queryData) return true;
    else return false;
  } else {
    //fallback query called when main recipe returns cards less than authored packs instead of hard coded 3 value
    if (defaultOffersReceipe !== DESTINATION_OFFERS_RECIPE) {
      if (isLightning === false) {
        if (!queryData?.offersCarousel?.length || queryData?.offersCarousel?.[0]?.offerCollection.length < noOfCards) {
          return true;
        } else return false;
      } else {
        if (
          !queryData?.marketing?.offers?.personalizedOffers?.offers?.length ||
          queryData?.marketing?.offers?.personalizedOffers?.offers?.[0]?.offerCollection.length < noOfCards
        ) {
          return true;
        } else return false;
      }
    } else if (isPOI || (!isStateEnabled && !isCountryEnabled)) {
      if (queryData?.offersSearchByGeolocation?.edges.length < noOfCards) {
        return true;
      } else return false;
    } else if ((!isStateEnabled && isCountryEnabled) || (isStateEnabled && !isCountryEnabled)) {
      if (queryData?.offersSearchByArea?.edges.length < noOfCards) {
        return true;
      } else return false;
    } else {
      return false;
    }
  }
};

export const objectToArray = (data: any) => {
  return Object.keys(data).map(key => ({
    name: key,
    value: String(data[key]), // Convert each value to a string
  }));
};

// Utility function to update Robot metaName
export const updateRobotMetaNames = (metaNames: { name: string; value: string }[]) => {
  return metaNames.map(meta => {
    if (meta.name === 'robots') {
      return { ...meta, value: NO_INDEX_FOLLOW };
    }
    return meta;
  });
};

const ERROR_TEXT_COMPONENT_TYPE = 'mi-aem-offers-spa/components/text';
const SCRUB_ERROR_QUERY_FLAGS = ['invalidDealMsg', 'invalidUserMsg'] as const;

export const hasErrorQueryFlag = (query: Record<string, unknown> = {}) => {
  return SCRUB_ERROR_QUERY_FLAGS.some(flag => String(query?.[flag]) === 'true');
};

export const scrubErrorTextFromModel = (node: any) => {
  if (!node || typeof node !== 'object') {
    return;
  }

  if (Array.isArray(node)) {
    node.forEach(item => scrubErrorTextFromModel(item));
    return;
  }

  const styleClasses = String(node?.styleclass ?? '')
    .split(' ')
    .filter(Boolean);
  const isErrorTextComponent =
    node[':type'] === ERROR_TEXT_COMPONENT_TYPE && SCRUB_ERROR_QUERY_FLAGS.some(flag => styleClasses.includes(flag));

  if (isErrorTextComponent && typeof node?.text === 'string') {
    node.text = '';
  }

  if (isErrorTextComponent && node?.dataLayer && typeof node.dataLayer === 'object') {
    Object.keys(node.dataLayer).forEach(dataLayerKey => {
      const dataLayerNode = node.dataLayer[dataLayerKey];
      if (dataLayerNode && typeof dataLayerNode === 'object' && typeof dataLayerNode['xdm:text'] === 'string') {
        dataLayerNode['xdm:text'] = '';
      }
    });
  }

  Object.keys(node).forEach(key => scrubErrorTextFromModel(node[key]));
};

// Utility function for targetting criteria
export const getTargettingCriteria = (
  workspaceId: string | number,
  isRecipePropNonStayOffers: boolean,
  propsMarshCode: any,
  resortRequestVariables: ResortRequestVariables,
  isRecipeEliteExclusive: boolean,
  isEliteUser: boolean,
  memberLevel: string,
  edgeHost: any,
  modifiedDataLayerPayload: any
) => {
  return {
    targetingCriteria: {
      workspaceId: workspaceId, // Adobe Target Locale specific workspace Id
      ...(isRecipePropNonStayOffers && { propertyIds: propsMarshCode }),
      ...resortRequestVariables,
      ...(isRecipeEliteExclusive && isEliteUser && { memberLevel }),
      ...(edgeHost && { edgeHost }),
      ...(modifiedDataLayerPayload && { parameters: modifiedDataLayerPayload }),
    },
  };
};

// Utility function for generating request Variables
export const generateRequestVariables = (
  defaultOffersReceipe: string,
  OFFER_CAROUSEL_CHANNEL: string,
  sourceUrl: any,
  OFFER_CAROUSEL_URL_REFERRER: string,
  searchCriteria: string,
  isEliteUser: boolean,
  isRecipeEliteExclusive: boolean,
  targetingCriteria: any,
  offersSearchInputVar: OffersSearchInputVar
) => {
  return {
    optimizedOffersInput: {
      action: defaultOffersReceipe,
      source: {
        channel: OFFER_CAROUSEL_CHANNEL,
        url: sourceUrl,
        urlReferrer: OFFER_CAROUSEL_URL_REFERRER,
      },
      searchCriteria: {
        id: searchCriteria,
        ...(isEliteUser && isRecipeEliteExclusive && { isIncludeGatedOffer: true }),
      },
      ...targetingCriteria,
    },
    ...offersSearchInputVar,
  };
};
// Utility function for generating request Variables for lightning
export const generateLightningRequestVariables = (
  defaultOffersReceipe: string,
  OFFER_CAROUSEL_CHANNEL: string,
  sourceUrl: any,
  OFFER_CAROUSEL_URL_REFERRER: string,
  searchCriteria: string,
  isEliteUser: boolean,
  isRecipeEliteExclusive: boolean,
  targetingCriteria: any
) => {
  return {
    input: {
      action: defaultOffersReceipe,
      source: {
        channel: OFFER_CAROUSEL_CHANNEL,
        url: sourceUrl,
        urlReferrer: OFFER_CAROUSEL_URL_REFERRER,
      },
      searchCriteria: {
        id: searchCriteria,
        ...(isEliteUser && isRecipeEliteExclusive && { isIncludeGatedOffer: true }),
      },
      ...targetingCriteria,
    },
  };
};

export const isAllowedForLocale = (locale: keyof typeof LOCALE_SUBDIRECTORY_MAPPING) => {
  return !CHINESE_LANG.includes(locale as string);
};

export const getMCMIDWithFlag = async () => {
  if (window?.alloy && typeof window?.alloy === 'function') {
  await window.alloy('getIdentity').then(function (result: any) {
    window._satellite['setVar']('ECID', result.identity.ECID);
  });
  return window._satellite['getVar']('ECID');
  }
};

export const getMediaDataTertiary = (media: any, imageDomainVal: any, mediaSource: string) => {
  const imageDomainPath = imageDomainVal ?? imageDomain;
  const isChinaDomain = imageDomainPath === 'https://cache.marriott.com.cn';
  const imgSrcPath = media?.tertiaryImage?.imageSrc
    ? isChinaDomain && !media.tertiaryImage.imageSrc.includes('zh_cn')
      ? getFallbackImg(config.FALLBACK_CN_IMG_URI, WIDE_ASPECT_RATIO)
      : media.tertiaryImage.imageSrc
    : '';

  return {
    renditions: [],
    fileReferenceBackgroundImage: mediaSource === TERTIARY_MEDIA ? media?.tertiaryImage?.imageSrc : '',
    altText:
      mediaSource === PRIMARY_MEDIA
        ? media?.primaryImage?.alternateDescription
        : mediaSource === TERTIARY_MEDIA
        ? media?.tertiaryImage?.alternateDescription
        : '',
    dynamic: true,
    assetPath: media?.tertiaryImage?.imageSrc ? `${imageDomainPath}${imgSrcPath}` : '',
  };
};
export const getPropValue = (
  authorMode: boolean,
  nodeValue: Node | string | number | object | any,
  modelValue: Node | string | number | object | any
) => (authorMode ? modelValue : nodeValue);

export function constructCobrandCTAUrl(offer: any, dataLayer?: { mr_id_alternate?: string }): string | null {
  if (!offer?.ctaLink) return null;
  const altCustIdValue = dataLayer?.mr_id_alternate ?? '';
  const ecidValueRaw = canUseDOM ? window?._satellite?.['getVisitorId']()?.['_fields']?.MCMID : null;
  const ecidValue = typeof ecidValueRaw === 'string' && ecidValueRaw ? ecidValueRaw : undefined;
  const mcdValue = offer?.trackingValue;
  const rpcCodeValue = offer?.rpcCode;
  const offerCode = offer?.offerCode?.toUpperCase() ?? '';

  // Helper to build params object and filter undefined, except alt_cust_id which should always be present as a string
  const buildParams = (paramsObj: Record<string, string | undefined>) => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(paramsObj)) {
      if (key === 'alt_cust_id') {
        result[key] = value ?? '';
      } else if (value !== undefined) {
        result[key] = value;
      }
    }
    return result;
  };

  // Chase logic
  if (offer.url && CHASE_RPC_CODES.has(rpcCodeValue) && offerCode.includes('ACQ')) {
    const paramsObj = buildParams({
      returnURL: stripDomain(offer.url),
      alt_cust_id: altCustIdValue,
      cookie_id: ecidValue,
      mcd: mcdValue,
      rpcCode: rpcCodeValue,
    });
    const params = new URLSearchParams(paramsObj);
    const separator = offer.ctaLink.includes('?') ? '&' : '?';
    return `${offer.ctaLink}${separator}${params.toString()}`;
  }

  // Amex logic
  if (AMEX_RPC_CODES.has(rpcCodeValue) && offerCode.includes('ACQ')) {
    const paramsObj = buildParams({
      rpcCode: rpcCodeValue,
      alt_cust_id: altCustIdValue,
      cookie_id: ecidValue,
      mcd: mcdValue,
    });
    const params = new URLSearchParams(paramsObj).toString();
    // Properly encode the full params string for use as a query parameter
    const encodedParams = encodeURIComponent(params);
    console.log('encodedParams-', encodedParams);
    // Always append with %3F as separator (encoded '?')
    return `${offer.ctaLink}%3F${encodedParams}`;
  }

  // Default fallback
  return offer.ctaLink;
}

export const updateMBEDataLayer = (servicedata: any) => {
  const dataLayer: any = {};
  dataLayer['marrOfferId'] = servicedata?.offerId;
  return dataLayer;
};

const stripDomain = (url: string): string => {
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return url;
  }
};

export function isRtlEnable({
  currentLocale,
  rtlEnable,
  uri,
}: {
  currentLocale?: string;
  rtlEnable?: boolean | string;
  uri?: string;
}): boolean {
  const rtlLangList = ['ar-AE'];
  if (!rtlEnable) {
    return false;
  }
  const rtlUriPatterns = [
    '/offers-mpo-mbo/',
    '/offers-mpo-sbo/',
    '/offers-mpo-mbo-preview/',
    '/offers-mpo-sbo-preview/',
    '/offers-spo/',
    '/offers-spo-preview/',
    '/offers',
    '/offers/findOffers',
    '/offers-dme-single-card/',
    '/offers-dme-single-card-preview/',
  ];
  return Boolean(
    rtlLangList.includes(currentLocale || '') && rtlEnable && rtlUriPatterns.some(pattern => uri?.includes(pattern))
  );
}

// Generic utility to get model property value with fallback
export const getModelPropValue = (obj: Record<string, any> | undefined | null, key: string, fallback?: any): any => {
  if (obj && obj?.[key] !== undefined && obj?.[key] !== null) {
    return obj[key];
  }
  return fallback !== undefined ? fallback : '';
};

//mpo alert labels utility
export const getAlertLabels = (type: string, alertsListArray: any) => {
  return alertsListArray?.find((alert: any) => alert?.alertMsgTypeName === type);
};
export const getWarningAlert = (type: string, alertsListArray: any, alertData?: any) => {
  const warningAlertConfig = getAlertLabels(type, alertsListArray);
  const warningAlert = {
    linkText: warningAlertConfig?.alertCodeListModel?.[0]?.alertLinkTextLabel ?? alertData?.CURRENCY_LINK_TEXT ?? '',
    errorMessage: warningAlertConfig?.alertCodeListModel?.[0]?.alertMessageLabel ?? alertData?.CURRENCY_MESSAGE ?? '',
    showPageAndFilter: true,
    icon: alertData?.ICON_TYPE ?? '',
    type: alertData?.Alert_TYPE ?? '',
    ...(type === 'alertCurrenyUpdateNotification' && {
      heading: warningAlertConfig?.alertCodeListModel?.[0]?.alertHeadingLabel ?? alertData?.CURRENCY_HEADING ?? '',
    }),
  };
  return warningAlert;
};
